import { PolIcon } from "components/PolIcon/policon";
import React, { useEffect } from 'react';
import ReactTooltip from "react-tooltip";
import Confirm from "v4/components/ui/Confirm/Confirm";
import PolDropdown from "v4/components/ui/PolDropdown/PolDropdown";
import usePennylaneButton from "v4/features/front/pennylane/components/PennylaneButton/hooks/usePennylaneButton";
import PennylaneConfirmCreate
    from "v4/features/front/pennylane/components/PennylaneConfirmCreate/PennylaneConfirmCreate";
import PennylaneConfirmUpdate
    from "v4/features/front/pennylane/components/PennylaneConfirmUpdate/PennylaneConfirmUpdate";
import PennylaneErrorModal from "v4/features/front/pennylane/components/PennylaneErrorModal/PennylaneErrorModal";
import { usePolTranslation } from "v4/hooks/usePolTranslation";
import './PennylaneButton.scss';

export default function PennylaneButton({ quote }) {
    const { t } = usePolTranslation();
    const {
        isActive,
        hasExternalRelation,
        hasLinkedInvoice,
        isLocked,
        lockReason,
        createDialog,
        updateDialog,
        handleAccessQuote,
        handleAccessInvoice,
        error,
        invoiceUrl,
        isLoading,
        clearError,
        retry,
    } = usePennylaneButton(quote);

    // react-tooltip v4 ne lie les listeners qu'au mount (bindListener) ; un data-for/data-tip
    // ajouté après coup (item passé disabled) n'est jamais lié. On force le re-scan.
    useEffect(() => {
        ReactTooltip.rebuild();
    }, [isLocked, hasLinkedInvoice]);

    if (!isActive) return null;

    const errorModal = (
        <PennylaneErrorModal status={error?.status}
                             errorData={error?.data}
                             invoiceUrl={invoiceUrl}
                             isLoading={isLoading}
                             onClose={clearError}
                             onRetry={retry}/>
    );

    if (!hasExternalRelation) {
        return (
            <>
                <button className="prospect-tab__prospect__header__action pennylane-button"
                        onClick={createDialog.open}
                        disabled={isLoading || isLocked}
                        data-for={isLocked ? 'pennylane-create-tooltip' : undefined}
                        data-tip={isLocked ? lockReason : undefined}>
                    <PolIcon icon={isLoading ? "circle-notch" : "pennylane-logo"}/>
                    {isLoading ? t('pennylane_sending') : 'PennyLane'}
                </button>
                {isLocked && <ReactTooltip id="pennylane-create-tooltip" effect="solid"/>}
                <Confirm isOpen={createDialog.isOpen}
                         title="pennylane_create_confirm_title"
                         cancelAction={createDialog.close}
                         confirmAction={createDialog.confirm}
                         isLoading={isLoading}
                         noLabel="cancel"
                         yesLabel="pennylane_create_confirm_submit">
                    <PennylaneConfirmCreate/>
                </Confirm>
                {errorModal}
            </>
        );
    }

    return (
        <>
            <PolDropdown className="pennylane-button"
                         anchorInline="right"
                         summaryRender={({ isOpen }) => (
                             <span className={`prospect-tab__prospect__header__action pennylane-button ${isOpen ? 'is-open' : ''}`}>
                                 <PolIcon icon="pennylane-logo"/>
                                 PennyLane
                                 <PolIcon icon="chevron-down"/>
                             </span>
                         )}
                         detailsRender={({ toggleIsOpen }) => {
                             const renderItem = ({ icon, label, action, disabledReason }) => (
                                 <div className={`pol-dropdown__details-item ${disabledReason ? 'is-disabled' : ''}`}
                                      onClick={disabledReason ? undefined : () => {
                                          action();
                                          toggleIsOpen();
                                      }}
                                      data-for={disabledReason ? 'pennylane-item-tooltip' : undefined}
                                      data-tip={disabledReason}>
                                     <PolIcon icon={icon}/>
                                     {label}
                                 </div>
                             );

                             return (
                                 <div className="pol-dropdown__details">
                                     {renderItem({
                                         icon: 'external-link',
                                         label: t('pennylane_access_quote'),
                                         action: handleAccessQuote,
                                     })}
                                     {renderItem({
                                         icon: 'external-link',
                                         label: t('pennylane_access_invoice'),
                                         action: handleAccessInvoice,
                                         disabledReason: hasLinkedInvoice ? undefined : t('pennylane_invoice_missing_tooltip'),
                                     })}
                                     {renderItem({
                                         icon: 'refresh',
                                         label: t('pennylane_update_data'),
                                         action: updateDialog.open,
                                         disabledReason: isLocked ? lockReason : undefined,
                                     })}
                                     <ReactTooltip id="pennylane-item-tooltip" effect="solid"/>
                                 </div>
                             );
                         }}
            />
            <Confirm isOpen={updateDialog.isOpen}
                     title="pennylane_update_confirm_title"
                     cancelAction={updateDialog.close}
                     confirmAction={updateDialog.confirm}
                     isLoading={isLoading}
                     noLabel="cancel"
                     yesLabel="pennylane_update_confirm_submit">
                <PennylaneConfirmUpdate/>
            </Confirm>
            {errorModal}
        </>
    );
}
